/**
 * Main Google Play API client
 */

import { promises as fs } from "fs";
import type { AuthData } from "../models/auth";
import type { DeviceInfo } from "../models/device";
import { Pixel3a } from "../devices/factory";
import { GPTokenExpiredError, GPNetworkError } from "../errors";
import { doRequest, createDefaultHeaders } from "../utils/network";
import { unmarshalResponseWrapper } from "../utils/parser";
import { AuthEndpoint } from "./endpoints/auth";
import { AppsEndpoint } from "./endpoints/apps";
import { LibraryEndpoint } from "./endpoints/library";
import { HttpStatusCode } from "../models/common";
import type { Payload } from "../gen_proto/googleplay_pb";

export class GooglePlayClient {
    public authData: AuthData;
    public deviceInfo: DeviceInfo;
    public sessionFile?: string;

    public readonly auth: AuthEndpoint;
    public readonly apps: AppsEndpoint;
    public readonly library: LibraryEndpoint;

    constructor(authData: AuthData, deviceInfo: DeviceInfo = Pixel3a) {
        this.authData = authData;
        this.deviceInfo = deviceInfo;

        // Initialize endpoints
        this.auth = new AuthEndpoint(
            this.authData,
            this.deviceInfo,
            () => this.getAuthHeaders(),
            (url, options) => this.doAuthedReq(url, options)
        );
        
        this.apps = new AppsEndpoint(
            (url, options) => this.doAuthedReq(url, options)
        );

        this.library = new LibraryEndpoint(
            (url, options) => this.doAuthedReq(url, options),
            (packageName) => this.getAppDetails(packageName)
        );
    }

    static async newClient(email: string, aasToken: string): Promise<GooglePlayClient> {
        return GooglePlayClient.newClientWithDeviceInfo(email, aasToken, Pixel3a);
    }

    static async newClientWithDeviceInfo(
        email: string,
        aasToken: string,
        deviceInfo: DeviceInfo,
    ): Promise<GooglePlayClient> {
        const authData: AuthData = {
            email,
            aasToken,
            locale: "en_GB",
        };

        const client = new GooglePlayClient(authData, deviceInfo);

        await client.auth.generateGsfId();

        const deviceConfigRes = await client.auth.uploadDeviceConfig();
        authData.deviceConfigToken = deviceConfigRes.uploadDeviceConfigToken;

        const token = await client.auth.generateGPToken();
        authData.authToken = token;

        await client.auth.toc();

        return client;
    }

    async saveSession(file: string): Promise<void> {
        await fs.writeFile(file, JSON.stringify(this.authData, null, 2));
    }

    static async loadSession(file: string): Promise<GooglePlayClient> {
        return GooglePlayClient.loadSessionWithDeviceInfo(file, Pixel3a);
    }

    static async loadSessionWithDeviceInfo(file: string, deviceInfo: DeviceInfo): Promise<GooglePlayClient> {
        const data = await fs.readFile(file, "utf-8");
        const authData: AuthData = JSON.parse(data);
        const client = new GooglePlayClient(authData, deviceInfo);
        return client;
    }

    private async _doAuthedReq(url: string, options: RequestInit = {}): Promise<Payload | null> {
        const { data, status } = await doRequest(url, {
            ...(options as Omit<RequestInit, "headers">),
            headers: {
                ...this.getDefaultHeaders(),
                ...((options.headers as Record<string, string>) || {}),
            },
        });

        if (status === HttpStatusCode.UNAUTHORIZED) {
            throw new GPTokenExpiredError();
        }

        if (status >= 400) {
            throw new GPNetworkError(`Request failed with status ${status}`, status);
        }

        const resp = await unmarshalResponseWrapper(data);
        return resp.payload || null;
    }

    async doAuthedReq(url: string, options: RequestInit = {}): Promise<Payload | null> {
        try {
            return await this._doAuthedReq(url, options);
        } catch (error) {
            if (error instanceof GPTokenExpiredError) {
                await this.auth.regenerateGPToken();
                if (this.sessionFile) {
                    await this.saveSession(this.sessionFile);
                }
                return await this._doAuthedReq(url, options);
            }
            throw error;
        }
    }

    private getAuthHeaders(): Record<string, string> {
        return {
            App: "com.google.android.gms",
            "User-Agent": this.deviceInfo.authUserAgent,
            ...(this.authData.gsfId && { Device: this.authData.gsfId }),
        };
    }

    private getDefaultHeaders(): Record<string, string> {
        const data = this.authData;
        const headers = createDefaultHeaders(
            this.deviceInfo.userAgent,
            data.gsfId!,
            data.authToken
        );

        if (data.deviceCheckInConsistencyToken) {
            headers["X-Dfe-Device-Checkin-Consistency-Token"] = data.deviceCheckInConsistencyToken;
        }

        if (data.deviceConfigToken) {
            headers["X-Dfe-Device-Config-Token"] = data.deviceConfigToken;
        }

        if (data.dfeCookie) {
            headers["X-Dfe-Cookie"] = data.dfeCookie;
        }

        if (this.deviceInfo.simOperator) {
            headers["X-Dfe-Mccmnc"] = this.deviceInfo.simOperator;
        }

        return headers;
    }

    // Convenience methods
    async getAppDetails(packageName: string) {
        return this.apps.getAppDetails(packageName);
    }

    async getBuyResponse(packageName: string, version: number) {
        return this.library.getBuyResponse(packageName, version);
    }

    async getDeliveryResponse(packageName: string, version: number) {
        return this.library.getDeliveryResponse(packageName, version);
    }

    async purchase(packageName: string, version?: number) {
        return this.library.purchase(packageName, version);
    }

    async generateGsfId(): Promise<string> {
        return this.auth.generateGsfId();
    }

    async regenerateGPToken(): Promise<void> {
        return this.auth.regenerateGPToken();
    }
}